import { ComponentRef, ApplicationRef, EnvironmentInjector, createComponent, inject } from '@angular/core';
import { BoundingBox } from '@newroom-connect/library/types';
import { LoggingService } from '@newroom-connect/library/services';
import { ISphericalCoords } from '@newroom-connect/library/interfaces';
import { TransformCoordinateHelper } from '@newroom-connect/library/helpers';

import { HotspotDragOverlayComponent, IDragPointerEvent } from '../components/hotspot-drag-overlay/hotspot-drag-overlay.component';
import { MarzipanoScene } from '../models/marzipano-scene.model';

// Click-related event payloads/callbacks for the drag overlay
export interface IDragClickEvent {
  id: string;
  event: MouseEvent;
}

export interface IDragMoveEvent {
  id: string;
  position: ISphericalCoords;
}

export interface IDragOverlayEvents {
  onClick?: (payload: IDragClickEvent) => void;
  onDoubleClick?: (payload: IDragClickEvent) => void;
  onClickOutside?: (payload: IDragClickEvent) => void;
  onDragMove: ((event: IDragMoveEvent) => void) | null;
  onDragStart?: () => void;
  onDragEnd?: () => void;
}

/**
 * Drag Overlay Manager.
 *
 * Handles drag overlay creation, positioning, and drag events.
 *
 */
export class DragOverlay {
  private readonly appRef = inject(ApplicationRef);
  private readonly envInjector = inject(EnvironmentInjector);
  private readonly logger = inject(LoggingService);

  private boundingBox: BoundingBox | null = null;
  private hotspotId: string | null = null;
  private scene: MarzipanoScene | null = null;

  private onClick: ((payload: IDragClickEvent) => void) | null = null;
  private onDoubleClick: ((payload: IDragClickEvent) => void) | null = null;
  private onClickOutside: ((payload: IDragClickEvent) => void) | null = null;
  private onDragMove: ((payload: IDragMoveEvent) => void) | null = null;
  private onDragStart: (() => void) | null = null;
  private onDragEnd: (() => void) | null = null;

  private overlayComponent: ComponentRef<HotspotDragOverlayComponent> | null = null;

  /**
   * Create drag overlay with Angular component.
   *
   * @param hotspotId - The unique identifier for the hotspot.
   * @param boundingBox - The bounding box data.
   * @param hotspotElement - The HTML element of the hotspot.
   * @param viewerElement
   * @param scene - The Marzipano scene for coordinate conversion.
   * @param events - Event handlers for click interactions.
   *
   * @returns The created container element or null if there is an error.
   */
  public createDragOverlayComponent(
    hotspotId: string,
    boundingBox: BoundingBox,
    hotspotElement: HTMLElement,
    viewerElement: HTMLElement,
    scene: MarzipanoScene,
    events?: IDragOverlayEvents
  ): HTMLElement {
    const baseElement = this.createDragOverlayBaseElement(hotspotElement);

    try {
      if (!hotspotElement || !viewerElement) {
        return baseElement;
      }

      // Store references and callbacks
      this.hotspotId = hotspotId;
      this.boundingBox = boundingBox;
      this.scene = scene;

      this.onDragMove= events?.onDragMove ?? null;
      this.onClick = events?.onClick ?? null;
      this.onDoubleClick = events?.onDoubleClick ?? null;
      this.onClickOutside = events?.onClickOutside ?? null;
      this.onDragStart = events?.onDragStart ?? null;
      this.onDragEnd = events?.onDragEnd ?? null;

      // Calculate start offset center
      const startOffset = this.calculateStartOffset(boundingBox, viewerElement, scene);

      // Create Angular component
      const compRef = createComponent(HotspotDragOverlayComponent, { environmentInjector: this.envInjector });

      // Configure component
      compRef.instance.hotspotId = hotspotId;
      compRef.instance.viewerElement = viewerElement;
      compRef.instance.startOffsetX = startOffset.offsetX;
      compRef.instance.startOffsetY = startOffset.offsetY;
      compRef.instance.draggable = true;

      // Wire drag and click events
      this.setupDragEvents(compRef, scene);

      // Mount component
      this.appRef.attachView(compRef.hostView);

      // Store the overlay component for future reference
      this.overlayComponent = compRef;

      // Append the overlay component to the base element
      baseElement.appendChild(compRef.location.nativeElement);

      return baseElement;
    } catch (error) {
      this.logger.error('Failed to create drag overlay', error as Error);
      return baseElement;
    }
  }

  /**
   * Destroy the drag overlay and clean up resources.
   */
  public destroy(): void {
    if (this.overlayComponent) {
      this.appRef.detachView(this.overlayComponent.hostView);
      this.overlayComponent.destroy();

      this.overlayComponent = null;
    }

    this.hotspotId = null;
    this.boundingBox = null;
  }

  /**
   * Show the drag overlay by making it visible.
   */
  public show(): void {
    if (this.overlayComponent) {
      const baseElement = this.overlayComponent.location.nativeElement.parentElement;

      if (baseElement) {
        baseElement.style.display = 'block';
      }

      // Trigger change detection to ensure component updates
      this.overlayComponent.changeDetectorRef.detectChanges();
    }
  }

  /**
   * Hide the drag overlay by making it invisible.
   */
  public hide(): void {
    if (this.overlayComponent) {
      const baseElement = this.overlayComponent.location.nativeElement.parentElement;

      if (baseElement) {
        baseElement.style.display = 'none';
      }

      // Trigger change detection to ensure component updates
      this.overlayComponent.changeDetectorRef.detectChanges();
    }
  }

  /**
   * Set draggable state for the component.
   *
   * @param enabled - Whether dragging should be enabled.
   */
  public setDraggable(enabled: boolean): void {
    if (this.overlayComponent) {
      this.overlayComponent.instance.draggable = enabled;
      this.overlayComponent.changeDetectorRef.detectChanges();
    }
  }

  /**
   * Update overlay dimensions when hotspot is resized.
   *
   * @param width - New width in pixels.
   * @param height - New height in pixels.
   */
  public updateSize(width: number, height: number): void {
    if (!this.overlayComponent) {
      return;
    }

    // Update the base element size
    const baseElement = this.overlayComponent.location.nativeElement.parentElement;

    if (baseElement) {
      baseElement.style.width = `${width}px`;
      baseElement.style.height = `${height}px`;
    }

    // Trigger change detection
    this.overlayComponent.changeDetectorRef.detectChanges();
  }

  /**
   * Clean up the drag overlay and remove the overlay component.
   */
  public cleanupOverlay(): void {
    if (this.overlayComponent) {
      this.appRef.detachView(this.overlayComponent.hostView);
      this.overlayComponent.destroy();
      this.overlayComponent = null;
    }

    this.onDragMove = null;
    this.onClick = null;
    this.onDoubleClick = null;
    this.onClickOutside = null;
  }

  /**
   * Setup drag and click event handlers for the drag overlay component.
   *
   * @param compRef - Component reference to the drag overlay.
   * @param scene - The Marzipano scene for coordinate conversion.
   */
  private setupDragEvents(compRef: ComponentRef<HotspotDragOverlayComponent>, scene: MarzipanoScene): void {
    // Drag start events (disable camera controls)
    compRef.instance.dragStartEvent.subscribe(() => {
      try {
        this.onDragStart?.();
      } catch (error) {
        this.logger.error('Failed to handle drag start', error as Error);
      }
    });

    // Drag end events (re-enable camera controls)
    compRef.instance.dragEndEvent.subscribe(() => {
      try {
        this.onDragEnd?.();
      } catch (error) {
        this.logger.error('Failed to handle drag end', error as Error);
      }
    });

    // Drag move events - convert pointer coordinates to spherical coordinates
    compRef.instance.dragMoveEvent.subscribe((pointerCoords: IDragPointerEvent) => {
      try {
        if (!this.hotspotId || !this.boundingBox || !scene) {
          return;
        }

        // Convert pointer coordinates to spherical coordinates
        const sphericalCoords = this.pointerToSpherical(pointerCoords, scene);

        this.boundingBox.spherical = {
          ...this.boundingBox.spherical,
          yaw: sphericalCoords.yaw,
          pitch: sphericalCoords.pitch
        };

        this.onDragMove?.({ id: this.hotspotId, position: sphericalCoords });
      } catch (error) {
        this.logger.error('Failed to handle drag move', error as Error);
      }
    });

    // Click events
    compRef.instance.clickEvent.subscribe((event) => {
      try {
        if (this.onClick && this.hotspotId) {
          this.onClick({ id: this.hotspotId, event });
        }
      } catch (error) {
        this.logger.error('Failed to handle click event', error as Error);
      }
    });

    // Double click events
    compRef.instance.doubleClickEvent.subscribe((event) => {
      try {
        if (this.onDoubleClick && this.hotspotId) {
          this.onDoubleClick({ id: this.hotspotId, event });
        }
      } catch (error) {
        this.logger.error('Failed to handle double click event', error as Error);
      }
    });

    // Click outside events
    compRef.instance.clickOutsideEvent.subscribe((event) => {
      try {
        // Ignore clicks originating from the transform overlay (corner dots)
        const target = event.target as HTMLElement | null;

        if (target && this.isInsideTransformOverlay(target)) {
          return;
        }

        if (this.onClickOutside && this.hotspotId) {
          this.onClickOutside({ id: this.hotspotId, event });
        }
      } catch (error) {
        this.logger.error('Failed to handle click outside event', error as Error);
      }
    });
  }

  /**
   * Calculate the start offset for drag operations.
   *
   * @param boundingBox - The bounding box data.
   * @param viewerElement
   * @param scene - The Marzipano scene.
   * @returns The calculated offset.
   */
  private calculateStartOffset(boundingBox: BoundingBox, viewerElement: HTMLElement, scene: MarzipanoScene): { offsetX: number; offsetY: number } {
    try {
      const spherical = TransformCoordinateHelper.getSphericalPositionFromBoundingBox(boundingBox);

      if (!viewerElement || !spherical || !scene) {
        return { offsetX: 0, offsetY: 0 };
      }

      const center = scene.coordinateService.sphericalToScreen({ yaw: spherical.yaw, pitch: spherical.pitch });

      if (center) {
        return {
          offsetX: center.x,
          offsetY: center.y
        };
      }

      return { offsetX: 0, offsetY: 0 };
    } catch {
      return { offsetX: 0, offsetY: 0 };
    }
  }

  /**
   * Convert pointer coordinates to spherical coordinates.
   *
   * @param pointerCoords - The pointer coordinates.
   * @param scene - The Marzipano scene.
   * @returns The spherical coordinates.
   */
  private pointerToSpherical(pointerCoords: IDragPointerEvent, scene: MarzipanoScene): ISphericalCoords {
    try {
      const converted = scene.coordinateService.screenToSpherical({ x: pointerCoords.x, y: pointerCoords.y });

      return converted || { yaw: 0, pitch: 0 };
    } catch {
      return { yaw: 0, pitch: 0 };
    }
  }

  /**
   * Detect if a DOM target is inside the transform overlay UI for this hotspot.
   * Used to prevent clickOutside from firing when interacting with corner dots.
   * @param target
   *
   * @returns
   */
  private isInsideTransformOverlay(target: HTMLElement): boolean {
    const TRANSFORM_OVERLAY_CLASS = 'nrc-hotspot-transform-overlay';
    const CORNER_DOT_CLASS = 'corner-dot';
    const CORNER_CONTAINER_CLASS = 'corner-dots-container';

    let element: HTMLElement | null = target;

    while (element) {
      const classList = element.classList || { contains: () => false };

      if (
        classList.contains(TRANSFORM_OVERLAY_CLASS) ||
        classList.contains(CORNER_DOT_CLASS) ||
        classList.contains(CORNER_CONTAINER_CLASS)
      ) {
        return true;
      }

      element = element.parentElement;
    }

    return false;
  }

  /**
   * Create the drag overlay base element to host drag UI.
   *
   * @param hotspotElement - The base hotspot element for sizing reference.
   *
   * @returns The created container element.
   */
  private createDragOverlayBaseElement(hotspotElement: HTMLElement): HTMLElement {
    // Calculate dimensions
    const width = hotspotElement?.offsetWidth || 200;
    const height = hotspotElement?.offsetHeight || 150;

    // Create container for overlay UI
    const container = document.createElement('div');

    container.className = 'nrc-hotspot-drag-overlay';
    container.style.position = 'relative';
    container.style.width = `${width}px`;
    container.style.height = `${height}px`;
    // Remove z-index - will be handled by Marzipano layering system

    // Start hidden - visibility will be controlled by show/hide methods
    container.style.display = 'none';

    return container;
  }
}
